import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import useGetAttachmentUploadUrl from '#/common/components/AttachmentUpload/hooks/useAttachmentUploadUrlMutation';
import useAttachmentCreate from '#/common/components/AttachmentUpload/hooks/useAttachmentCreateMutation';

/**
 * Uploads a CSV import file as an Attachment and returns its ID so it can be sent to the
 * backend for processing. Shared by every data-import flow (Asset, Work Order, ...).
 */
const useImportFileUpload = () => {
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();

  const uploadInputFile = useCallback(
    async (file: File | null, tenantId?: string): Promise<string | undefined> => {
      if (!file) return;

      const relatedTable = 'data_import'; // NOSONAR TODO: No such table yet. Replace with correct related table when created.
      const relatedItem = uuidv4(); // NOSONAR TODO: Using a random UUID. Replace with correct related item ID when table is created.

      const uploadInfos = await getAttachmentUploadUrls(
        [
          {
            fileName: file.name,
            size: file.size,
            contentType: file.type,
            relatedTable,
            relatedItem,
            type: 'general',
          },
        ],
        tenantId,
      );

      const uploadInfo = Array.isArray(uploadInfos)
        ? uploadInfos.find((u: any) => u?.uploadUrl && u.uploadName)
        : undefined;

      if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(uploadInfo.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      const attachment = await createAttachment({
        fileName: String(uploadInfo.uploadName).split('/').pop() || '',
        size: file.size,
        mimeType: file.type,
        relatedTable,
        relatedItem,
        type: 'general',
        path: uploadInfo.uploadName,
        originalFileName: uploadInfo.originalFileName,
        tenantId,
      });

      if (!attachment?.id || !attachment.downloadUrl) {
        throw new Error('Failed to create attachment record');
      }

      return attachment.id;
    },
    [getAttachmentUploadUrls, createAttachment],
  );

  return { uploadInputFile };
};

export default useImportFileUpload;
